import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, HelpCircle, Lock, ShieldCheck, Sparkles, UploadCloud } from "lucide-react";
import type { ArticleHub } from "../content/personas";
import { ARTICLE_HUBS } from "../content/personas";
import { ContextualLinks } from "../components/ContextualLinks";

export function ArticlePage({ hub }: { hub: ArticleHub }) {
  const others = ARTICLE_HUBS.filter((h) => h.slug !== hub.slug);

  return (
    <article className="panel legal" style={{ maxWidth: 780, margin: "0 auto" }}>
      <header style={{ marginBottom: 24 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", background: "rgba(0, 255, 102, 0.1)", borderRadius: 999, border: "1px solid rgba(0, 255, 102, 0.25)", fontSize: 12, color: "#7cff5c", fontFamily: "ui-monospace, monospace", marginBottom: 12 }}>
          <Sparkles size={12} /> {hub.badge}
        </div>
        <h1 style={{ fontSize: "clamp(26px, 4vw, 36px)", lineHeight: 1.15, margin: "0 0 14px", fontWeight: 800 }}>
          {hub.h1}
        </h1>
        <p className="hint" style={{ fontSize: 15, lineHeight: 1.6, margin: 0 }}>
          {hub.lead}
        </p>
      </header>

      {/* Instant Action CTA + Visual Sample Transfer Card (Theme-Adaptive) */}
      <div style={{ padding: 24, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", marginBottom: 32 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20, alignItems: "center" }}>
          <div style={{ textAlign: "left" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, color: "var(--lime)" }}>
              <UploadCloud size={22} />
              <b style={{ fontSize: 16 }}>Send up to 5 GB free right now</b>
            </div>
            <p className="hint" style={{ margin: "0 0 16px" }}>
              Up to 500 files or folders · Client-side AES-256-GCM encryption · Need more? Send up to 1 TB with Exceed.
            </p>
            <Link to="/" className="btn block" style={{ textDecoration: "none" }}>
              <span>Start Free Transfer</span> <ArrowRight size={16} />
            </Link>
          </div>

          <div style={{ padding: 14, background: "var(--panel-solid)", border: "1px solid var(--border)", borderRadius: 12, fontSize: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <b style={{ color: "var(--lime)" }}>Sample Encrypted Package</b>
              <span style={{ fontFamily: "ui-monospace, monospace", color: "var(--muted)" }}>3.4 GB / 5.0 GB Free</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, color: "var(--text)", fontFamily: "ui-monospace, monospace" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span>project/master_cut_4k.mov</span><span className="size">1.9 GB</span></div>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span>project/raw_selects.zip</span><span className="size">1.1 GB</span></div>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span>project/print_deck.pdf</span><span className="size">390 MB</span></div>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span>project/notes_nda.txt</span><span className="size">4 KB</span></div>
            </div>
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", color: "var(--lime)", fontWeight: 700 }}>
              <span>✓ Zero-Knowledge Sealed</span>
              <span>limeshare.org/d/…#1.k…</span>
            </div>
          </div>
        </div>
      </div>

      <section style={{ marginBottom: 28 }}>
        <h2>Key advantages</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {hub.keyPoints.map((kp, i) => (
            <div key={i} style={{ padding: 14, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: 10 }}>
              <b style={{ color: "var(--lime)", display: "block", marginBottom: 4, fontSize: 15 }}>
                {kp.title}
              </b>
              <span className="hint" style={{ fontSize: 14, color: "var(--text)" }}>{kp.desc}</span>
            </div>
          ))}
        </div>
      </section>

      {hub.comparisonTable && (
        <section style={{ marginBottom: 28 }}>
          <h2>Feature comparison</h2>
          <table>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Traditional methods</th>
                <th>LimeShare</th>
              </tr>
            </thead>
            <tbody>
              {hub.comparisonTable.map((row, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{row.label}</td>
                  <td>{row.traditional}</td>
                  <td style={{ color: "var(--lime)", fontWeight: 600 }}>{row.limeshare}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section style={{ marginBottom: 28 }}>
        <h2>Step-by-step guide</h2>
        <ul style={{ display: "flex", flexDirection: "column", gap: 10, paddingLeft: 0, listStyle: "none" }}>
          {hub.steps.map((st, i) => (
            <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <CheckCircle2 size={16} color="#00ff66" style={{ flexShrink: 0, marginTop: 3 }} />
              <span>
                <b>{st.name}:</b> {st.text}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section style={{ marginBottom: 32 }}>
        <h2>Frequently asked questions</h2>
        {hub.faq.map((f, i) => (
          <div key={i} style={{ marginBottom: 16 }}>
            <h3 style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 4px", fontSize: 15 }}>
              <HelpCircle size={15} color="var(--lime)" /> {f.q}
            </h3>
            <p className="hint" style={{ margin: 0 }}>{f.a}</p>
          </div>
        ))}
      </section>

      <ContextualLinks currentSlug={hub.slug} />
    </article>
  );
}
